/* -----------------UI--------------- */
import theme from "@configs/theme";
import type { Stat } from "components/RowStats";
import type { TaskCardProps } from "../types";

import { getParentMetadata } from "@helpers/content/metadata";

/* -----------------Types--------------- */
import type { Distribution } from "@memorang/types";
import type { DiagnosticCardProps } from "types/tag";

/* -----------------Child components--------------- */
import { useExamContext } from "@features/exam";

import { isScreenshotMode } from "@constants/common";
import { praxisLogo } from "@constants/demo";
import type { ProethicaTestKind } from "@constants/demo/proethica-tests";
import { mockedHomePageHeaderStats } from "@constants/mocked-data";
import { hasProductAccess } from "@helpers/content/tag";
import { randomizeDistribution } from "@helpers/mocked-helpers";
import { isWeb } from "@helpers/platform";
import useAppConfigIds from "@hooks/useAppConfigIds";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useProductMetadata } from "@hooks/useProduct";
import { router } from "expo-router";
import type { TFunction } from "i18next";
import { useState } from "react";
/* -----------------i18n--------------- */
import { useTranslation } from "react-i18next";
/* -----------------API Calls--------------- */
import { getWeakTagsIds } from "../helpers/common";
import {
	getProethicaCompletionDistribution,
	getProethicaCompletionStats,
	getProethicaHeaderStats,
} from "../helpers/proethica-home-stats";
import {
	getProethicaModuleListProps,
	getProethicaModuleTags,
	getProethicaOrderedEntries,
} from "../helpers/proethica-module-list";
import { buttonIconForStatus } from "../helpers/proethica-recommendations";
import { getExamDaysLeft, getExamStatus } from "../helpers/stats";
import type { HomeSection } from "../types";
import { useMasteryKeyTopicsSectionData } from "./useMasteryKeyTopicsSectionData";
import { useProethicaModuleList } from "./useProethicaModuleList";
import { useProethicaRecommendations } from "./useProethicaRecommendations";
import { useRecommendationSection } from "./useRecommendationSection";

type GetHeaderDataProps = {
	distribution: Distribution;
	examIcon?: string;
	examName?: string;
	examDate?: string;
	isDarkMode?: boolean;
	onDateStatItemClick?: () => void;
	t: TFunction;
	showPracticeWeak?: boolean;
	hasAccess?: boolean;
	accuracy?: number;
	demoExamId?: string;
};

const getHeaderData = ({
	distribution,
	examName,
	examDate,
	isDarkMode,
	onDateStatItemClick,
	t,
	accuracy,
	examIcon,
}: GetHeaderDataProps) => {
	if (!distribution) {
		return undefined;
	}
	const examStatusText = getExamStatus(examDate);

	const answered =
		Object.values(distribution).reduce((a, b) => a + b, 0) - distribution.none;
	const masteryPercentage = accuracy ? Math.round(accuracy) : 0;

	const daysLeft = getExamDaysLeft(examDate);

	const headerStats: Stat[] = [
		{
			icon: "brain",
			value: t("dashboard.stats.masteryValue", { value: masteryPercentage }),
			label: t("dashboard.stats.mastery"),
			color: theme(isDarkMode).palette.purple.light,
		},
		{
			icon: "check-circle-outline",
			value: t("dashboard.stats.completedValue", { count: answered }),
			label: t("dashboard.stats.completed"),
			color: theme(isDarkMode).palette.success.light,
		},
		{
			label: examDate
				? t("dashboard.stats.untilExam")
				: t("dashboard.stats.examDate"),
			icon: "calendar-outline",
			color: theme(isDarkMode).colors.primary,
			labelColor: theme(isDarkMode).colors.primary,
			onClick: onDateStatItemClick,
			value: examDate
				? daysLeft && daysLeft > 0
					? t("dashboard.stats.daysLeft", { count: daysLeft })
					: t("dashboard.stats.pastDue")
				: t("dashboard.stats.setExamDate"),
		},
	];

	return {
		distribution: isScreenshotMode
			? randomizeDistribution(distribution)
			: distribution,
		headerStats: isScreenshotMode
			? mockedHomePageHeaderStats(isDarkMode)
			: headerStats,
		masteryPercentage,
		examIcon,
		examName,
		examStatusText,
		examDate,
	};
};

const homeMenus = [
	{
		type: "store" as const,
		label: "Store",
		icon: "cart-outline",
	},
	{
		type: "settings" as const,
		label: "Settings",
		icon: "cog-outline",
	},
];

export const useHome = () => {
	const { t } = useTranslation();
	const {
		currentExamContext: { examDate },
	} = useExamContext();

	const theme = useAppTheme();

	const isDarkMode = theme.dark;

	const { data, isLoading } = useMixedItemDist();
	const [showDatePicker, setShowDatePicker] = useState(false);

	const [selectedWeakTags, setSelectedWeakTags] = useState<
		string[] | undefined
	>(undefined);

	const toggleShowDatePicker = () => {
		setShowDatePicker((prev) => !prev);
	};

	const mixedItemDist = data;

	const {
		data: productMetadata,
		isLoading: isProductMetadataLoading,
		isFetched: isProductMetadataFetched,
	} = useProductMetadata();

	const { productUuid } = useAppConfigIds();

	const finalIsLoading = isLoading || isProductMetadataLoading;

	const hasAccess = hasProductAccess(productMetadata, productUuid);
	const demoCurrentExam = useDemoExamStore((state) => state.currentExam);
	const { liveStatusFor, resolveModuleItems, startModule, startingModuleId } =
		useProethicaModuleList();
	// const hasAccess = false;
	const contents = mixedItemDist?.contents || [];
	const numItems = contents.reduce((acc, curr) => acc + curr.totalCount, 0);

	const { distribution } = getParentMetadata(contents, numItems);
	const weakTagsIds = mixedItemDist ? getWeakTagsIds(mixedItemDist) : [];

	const headerData = mixedItemDist
		? getHeaderData({
				distribution,
				examIcon: theme.dark ? praxisLogo.light : praxisLogo.dark,
				examName: demoCurrentExam?.name,
				examDate,
				isDarkMode,
				onDateStatItemClick: toggleShowDatePicker,
				t,
				accuracy: mixedItemDist.accuracy,
				demoExamId: demoCurrentExam?.id,
			})
		: undefined;

	const exams = mixedItemDist?.exams;

	const handleSessionConfigNavigation = (tagIds: string[]) => {
		if (isWeb) {
			setSelectedWeakTags(tagIds);
		} else {
			router.push({
				pathname: "/session/customize",
				params: {
					selectedTagIds: tagIds,
				},
			});
		}
	};

	const masteryKeyTopicsSectionData =
		useMasteryKeyTopicsSectionData(mixedItemDist);

	const recommendationSectionHookData = useRecommendationSection(
		mixedItemDist ? distribution : undefined,
	);

	const genericRecommendationItems = (recommendationSectionHookData
		?.tasksSection?.data ?? []) as TaskCardProps[];
	const moduleTags = getProethicaModuleTags(mixedItemDist?.exams);
	const gatedOrderedEntries = resolveModuleItems(
		getProethicaOrderedEntries(mixedItemDist?.exams),
	);
	const isProethica = demoCurrentExam?.id === "proethica";
	const proethicaRecommendationCards = useProethicaRecommendations();
	const proethicaCompletion =
		isProethica && moduleTags.length
			? getProethicaCompletionStats({ moduleTags, getStatus: liveStatusFor })
			: undefined;
	const finalHeaderData =
		headerData && proethicaCompletion
			? {
					...headerData,
					headerStats: getProethicaHeaderStats(
						proethicaCompletion,
						{
							purple: theme.palette.purple.main,
							primary: theme.colors.primary,
						},
						{
							examDate,
							onDateStatItemClick: toggleShowDatePicker,
							t,
						},
					),
					distribution: getProethicaCompletionDistribution(proethicaCompletion),
				}
			: headerData;
	type ProethicaRecommendation = TaskCardProps &
		DiagnosticCardProps & {
			id: string;
			testKind?: ProethicaTestKind;
		};
	// Roster order (Module 1, Pre-Test, M2..M8, Post-Test) - selection below is
	// index-based, so no status re-sort.
	const proethicaRecommendations: ProethicaRecommendation[] =
		gatedOrderedEntries.map((tag) => {
			const module = getProethicaModuleListProps(
				tag,
				t,
				liveStatusFor(tag),
				hasAccess,
			);
			// Completed = attempted = sum of non-`none` distribution buckets (mirrors
			// origin/main's ExamInfoCard/getAttemptedCount). A NOT_STARTED module has
			// an all-zero distribution, so this is 0 - using numItems here would read
			// it as fully complete.
			const numCompleted =
				module.status === "COMPLETE"
					? module.numItems
					: Object.values(tag.distribution).reduce((a, b) => a + b, 0) -
						(tag.distribution.none ?? 0);
			const buttonIcon = buttonIconForStatus(module.status);
			const handleClickTask = () => {
				// Test rows always dispatch through startModule, which routes a
				// completed test to its report (the progress tab excludes tests).
				if (module.testKind) {
					startModule(module.id);
					return;
				}
				if (module.status === "COMPLETE") {
					router.push("/(protected)/(tabs)/progress");
					return;
				}
				handleSessionConfigNavigation([module.id]);
			};
			return {
				id: module.id,
				testKind: module.testKind,
				title: module.title,
				subtitle: module.testKind
					? `${module.typeLabel} • ${module.numItems} items`
					: `${module.typeLabel} • ${module.durationLabel}`,
				status: module.status,
				handleClickTask,
				handleClickCard: handleClickTask,
				icon: "school-outline" as const,
				numItems: module.numItems,
				numCompleted,
				loading: startingModuleId === module.id,
				thumbnailSource: module.thumbnailSource ?? module.thumbnailUrl,
				hasAccess,
				disabled: module.disabled,
				cta: module.cta,
				button: {
					cta: module.cta,
					icon: buttonIcon,
					handleClick: handleClickTask,
				},
				body: module.testKind
					? module.subtitle
					: module.status === "ACTIVE"
						? "Continue this module from where you left off."
						: module.status === "COMPLETE"
							? "Review your results and key takeaways."
							: "Start this module to keep your momentum.",
			};
		});
	const activeRecommendations = isProethica
		? proethicaRecommendations
		: genericRecommendationItems;
	const followUpRecommendations = activeRecommendations.slice(1, 3);

	const showPurchaseDialog = recommendationSectionHookData?.showPurchaseDialog;
	const toggleShowPurchaseDialog =
		recommendationSectionHookData?.toggleShowPurchaseDialog;

	const showCollectUserDetailsDialog =
		recommendationSectionHookData?.showCollectUserDetailsDialog;
	const toggleShowCollectUserDetailsDialog =
		recommendationSectionHookData?.toggleShowCollectUserDetailsDialog;

	const olxExamEligibility = recommendationSectionHookData?.olxExamEligibility;
	const setOlxExamEligibility =
		recommendationSectionHookData?.setOlxExamEligibility;
	const hideModulesOnHome = false;
	const masterySection: HomeSection | null =
		masteryKeyTopicsSectionData && !hideModulesOnHome
			? {
					title: masteryKeyTopicsSectionData.sectionTitle,
					type: masteryKeyTopicsSectionData.type,
					loading: masteryKeyTopicsSectionData.loading,
					chipProps:
						"chipProps" in masteryKeyTopicsSectionData
							? masteryKeyTopicsSectionData.chipProps
							: undefined,
					onPress: masteryKeyTopicsSectionData.onPress,
					data: [
						{
							type: masteryKeyTopicsSectionData.type,
							items: masteryKeyTopicsSectionData.data,
						},
					],
				}
			: null;
	const sections: HomeSection[] = [
		...(!isProethica && followUpRecommendations.length
			? [
					{
						title: t("dashboard.recommendations.title"),
						type: "tasks",
						loading: recommendationSectionHookData?.tasksSection?.loading,
						onPress: undefined,
						data: [
							{
								type: "tasks",
								items: followUpRecommendations,
							},
						],
					} satisfies HomeSection,
				]
			: []),
		...(masterySection ? [masterySection] : []),
	];

	return {
		headerData: finalHeaderData,
		weakTagsIds,
		sections,
		homeMenus,
		showPurchaseDialog,
		toggleShowPurchaseDialog,
		olxExamEligibility,
		setOlxExamEligibility,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		isLoading: finalIsLoading,
		recommendationItems: isProethica ? proethicaRecommendationCards : undefined,
		showDatePicker,
		toggleShowDatePicker,
		hasAccess,
		isHasAccessLoading:
			isLoading || isProductMetadataLoading || !isProductMetadataFetched,
		handleSessionConfigNavigation,
		selectedWeakTags,
		setSelectedWeakTags,
		exams,
	};
};
